import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";
import Breadcrumbs from "../components/Breadcrumbs";
import MotionMedia from "../components/MotionMedia";
import { ONGOING_BY_SLUG, ONGOING_PROJECTS } from "../data/ongoingProjects";
import { videoJsonLd } from "../lib/videoSchema";
import { OngoingBadges } from "./OngoingProjects";
import { COMPANY } from "../content";

const SITE = "https://timefurniture.ae";

export default function OngoingProjectDetail() {
  const { slug } = useParams();
  const project = ONGOING_BY_SLUG[slug];

  usePageMeta({
    title: project
      ? `${project.title} — Ongoing Project | Time Furniture`
      : "Ongoing Projects | Time Furniture",
    description: project
      ? `${project.title} — an ongoing ${project.category.toLowerCase()} project by Time Furniture${project.location ? ` in ${project.location}` : ""}. ${project.summary.split(" The visuals")[0]} All imagery is design visualization.`
      : "",
    canonical: project ? `${SITE}/ongoing-projects/${project.slug}` : `${SITE}/ongoing-projects`,
    ogImage: project ? `${SITE}${project.images[0].src}` : undefined,
    jsonLd: project
      ? [
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
              { "@type": "ListItem", position: 2, name: "Ongoing Projects", item: `${SITE}/ongoing-projects` },
              { "@type": "ListItem", position: 3, name: project.title, item: `${SITE}/ongoing-projects/${project.slug}` },
            ],
          },
          videoJsonLd({
            key: project.videoKey,
            name: `${project.title} — ongoing project design visualization`,
            description: `${project.summary.split(" The visuals")[0]} Design visualization by Time Furniture.`,
            seconds: 10,
          }),
        ].filter(Boolean)
      : undefined,
  });

  if (!project) return <Navigate to="/ongoing-projects" replace />;

  const others = ONGOING_PROJECTS.filter((p) => p.slug !== project.slug).slice(0, 3);
  const waText = encodeURIComponent(
    `Hi Time Furniture, I saw the ${project.title} ongoing project on your website and would like to discuss a similar project.`
  );

  return (
    <div data-testid={`ongoing-detail-${project.slug}`} className="bg-[#FAF5EC]">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 pb-24 md:pb-32">
        <Breadcrumbs
          padTop="pt-5 md:pt-8"
          items={[
            { label: "Home", to: "/" },
            { label: "Ongoing Projects", to: "/ongoing-projects" },
            { label: project.title },
          ]}
        />

        <div className="mt-5 md:mt-7">
          <OngoingBadges testid={`ongoing-detail-badges-${project.slug}`} />
          <p className="mt-5 text-[10px] tracking-[0.28em] uppercase text-[#6E1F2C]">
            {project.location ? `${project.category} · ${project.location}` : project.category}
          </p>
          <h1
            data-testid={`ongoing-detail-h1-${project.slug}`}
            className="mt-3 font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#1C110C]"
          >
            {project.title}
          </h1>
          <p className="mt-6 max-w-3xl text-[#3E2A22] leading-[1.85] text-base md:text-lg">
            {project.summary}
          </p>
        </div>

        {/* Hero media — reserved video key, poster until final bundle arrives */}
        <div className="mt-10">
          <MotionMedia
            mediaKey={project.videoKey}
            alt={project.images[0].alt}
            mode="hero"
            aspectClass="aspect-[16/9]"
            testid={`ongoing-detail-media-${project.slug}`}
            eager
          />
          <p className="mt-2 text-[10px] tracking-[0.2em] uppercase text-[#7A6B5F]">
            Design visualization · {project.images[0].caption}
          </p>
        </div>

        {/* Gallery — remaining original stills */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8" data-testid={`ongoing-detail-gallery-${project.slug}`}>
          {project.images.slice(1).map((im, i) => (
            <figure key={im.src}>
              <div className="overflow-hidden bg-[#EDE1C8]">
                <img
                  src={im.src}
                  width={im.w}
                  height={im.h}
                  loading="lazy"
                  decoding="async"
                  alt={im.alt}
                  data-testid={`ongoing-detail-img-${project.slug}-${i}`}
                  className="w-full aspect-[16/11] object-cover"
                />
              </div>
              <figcaption className="mt-2 text-[10px] tracking-[0.2em] uppercase text-[#7A6B5F]">
                Design visualization · {im.caption}
              </figcaption>
            </figure>
          ))}
        </div>

        {/* Project-specific inquiry CTA */}
        <div className="mt-16 bg-[#1C110C] text-[#FAF5EC] p-8 md:p-12" data-testid={`ongoing-detail-cta-${project.slug}`}>
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#D4A963]">Start a Conversation</p>
          <h2 className="mt-4 font-serif-display text-2xl md:text-4xl leading-snug max-w-2xl">
            Like the direction of {project.title}?
          </h2>
          <p className="mt-4 max-w-2xl text-sm md:text-base text-[#EDE1C8]/75 leading-[1.85]">
            Tell us about your space — we&rsquo;ll visit, measure and reply with a
            transparent proposal. One contract, one accountable team, since 1993.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <Link
              to="/contact"
              data-testid={`ongoing-detail-cta-quote-${project.slug}`}
              className="inline-flex items-center justify-center gap-3 bg-[#D4A963] text-[#1C110C] px-9 py-4 text-[11px] tracking-[0.32em] uppercase font-medium hover:bg-[#c9a166] transition-colors"
            >
              Request a Quote
            </Link>
            <a
              href={`https://wa.me/${COMPANY.whatsapp}?text=${waText}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`ongoing-detail-cta-whatsapp-${project.slug}`}
              className="inline-flex items-center justify-center gap-3 border border-[#FAF5EC]/55 text-[#FAF5EC] px-9 py-4 text-[11px] tracking-[0.32em] uppercase hover:bg-[#FAF5EC] hover:text-[#1C110C] transition-colors"
            >
              WhatsApp Us
            </a>
          </div>
        </div>

        {/* Related services */}
        <div className="mt-14" data-testid={`ongoing-detail-services-${project.slug}`}>
          <p className="eyebrow mb-4">Related services</p>
          <div className="flex flex-wrap gap-3">
            {project.serviceLinks.map((s) => (
              <Link
                key={s.to}
                to={s.to}
                data-testid={`ongoing-detail-service-link-${project.slug}-${s.to.replace(/\//g, "")}`}
                className="inline-flex items-center gap-2 border border-[#6E1F2C]/30 text-[#6E1F2C] px-5 py-2.5 text-[10px] tracking-[0.24em] uppercase hover:bg-[#6E1F2C] hover:text-[#FAF5EC] transition-colors"
              >
                {s.label} <ArrowRight size={12} />
              </Link>
            ))}
          </div>
        </div>

        {/* More ongoing projects */}
        <div className="mt-16 border-t border-[#6E1F2C]/15 pt-12">
          <div className="flex items-center justify-between gap-4 mb-8">
            <h2 className="font-serif-display text-2xl md:text-3xl text-[#1C110C]">More ongoing projects</h2>
            <Link
              to="/ongoing-projects"
              data-testid="ongoing-detail-view-all"
              className="text-[10px] tracking-[0.28em] uppercase text-[#6E1F2C] hover:text-[#56151F] whitespace-nowrap"
            >
              View all →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {others.map((p) => (
              <Link
                key={p.slug}
                to={`/ongoing-projects/${p.slug}`}
                data-testid={`ongoing-detail-related-${p.slug}`}
                className="group block"
              >
                <div className="overflow-hidden bg-[#EDE1C8]">
                  <img
                    src={p.images[0].src}
                    loading="lazy"
                    alt={p.images[0].alt}
                    className="w-full aspect-[16/10] object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <p className="mt-3 text-[9px] tracking-[0.24em] uppercase text-[#6E1F2C]">{p.category}</p>
                <p className="mt-1 font-serif-display text-lg text-[#1C110C] group-hover:text-[#6E1F2C] transition-colors">{p.title}</p>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
